import { useHostElement } from "#ce/composables/useHostElement";
import { resolveFinder } from "#ce/shared/finder-association";
import { KINDS } from "#ce/apps/SpFinderOmnisearch/props.js";

const DEFAULT_KIND = "provider";

// The kind each host last rendered. The host outlives its field, so this is what tells a remount
// with another kind apart from a reconnect of the same one.
const lastKinds = new WeakMap();

function finderKind(host) {
  const finder = resolveFinder(host).element;

  // The property carries a deployment default, and a `kind` written before the wrapper upgraded.
  return finder?.kind ?? finder?.getAttribute("kind");
}

function resolveKind(kind, host) {
  const requested = kind ?? finderKind(host) ?? DEFAULT_KIND;

  if (KINDS.includes(requested)) {
    return requested;
  }

  console.warn(`sp-finder-omnisearch: unknown kind "${requested}", using "${DEFAULT_KIND}".`);
  return DEFAULT_KIND;
}

// A custom error belongs to the field that was shown when it was set, and the host keeps it.
function releaseErrorOnKindChange(host, resolved) {
  if (!host) {
    return;
  }

  const previous = lastKinds.get(host);

  lastKinds.set(host, resolved);

  if (previous && previous !== resolved) {
    host.setCustomValidity?.("");
  }
}

/**
 * Returns the omnisearch's kind: `kind` when given, else the `kind` property or attribute of the
 * enclosing `sp-finder`, else `provider`. An unknown value is reported and falls back to
 * `provider`. When the host renders another kind than it did last, the custom error it carries is
 * cleared; a reconnect of the same kind keeps it.
 *
 * @param {string} [kind] - the element's own `kind` prop
 * @returns {"provider"|"events"}
 */
export function useOmnisearchKind(kind) {
  const host = useHostElement();
  const resolved = resolveKind(kind, host);

  releaseErrorOnKindChange(host, resolved);

  return resolved;
}
